import { Search, SquarePen } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Conversation } from '~/pages/messages/Messages';

interface ConversationListProps {
  conversations: Conversation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  header: string;
  subheader?: ReactNode;
}

export default function ConversationList({
  conversations,
  selectedId,
  onSelect,
  header,
  subheader,
}: ConversationListProps) {
  const { t } = useTranslation('messages');
  const [search, setSearch] = useState('');
  const filtered = conversations.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.organizationName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="w-72 flex flex-col border-r border-neut-border shrink-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neut-border">
        <div>
          <span className="text-sm font-semibold text-neut-prim">{header}</span>
          {subheader}
        </div>
        <SquarePen size={16} className="text-neut-sec" />
      </div>

      <div className="px-3 py-2">
        <div className="flex items-center gap-2 bg-neut-bg rounded-md px-3 py-1.5">
          <Search size={14} className="text-neut-400 shrink-0" />
          <input
            type="text"
            placeholder={t('messages.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="text-sm bg-transparent outline-none w-full text-neut-prim placeholder:text-neut-400"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.map((conv) => (
          <button
            key={conv.id}
            onClick={() => onSelect(conv.id)}
            className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-neut-100 hover:bg-neut-50 transition-colors ${
              selectedId === conv.id
                ? 'bg-neut-50 border-l-2 border-l-prim'
                : 'border-l-2 border-l-transparent'
            }`}
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0 ${conv.avatarBg}`}
            >
              {conv.initials}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-neut-prim truncate block">
                {conv.name}
              </span>
              <p className="text-xs text-neut-500 truncate mt-0.5">
                {conv.organizationName}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
